import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../../api/client';
import { toast } from 'sonner';
import {
  Package,
  Plus,
  Trash2,
  Save,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Edit2,
  X,
  Layers,
  AlertCircle,
  CheckCircle,
  Clock,
  Search,
  Download,
  Copy,
  Box,
  Truck,
  Calculator,
  BarChart3
} from 'lucide-react';

// SKU color palette for visual consistency
const SKU_COLORS = [
  { fill: '#B5D4F4', stroke: '#185FA5', text: '#0C447C' },  // blue
  { fill: '#9FE1CB', stroke: '#0F6E56', text: '#085041' },  // green
  { fill: '#FAC775', stroke: '#854F0B', text: '#633806' },  // amber
  { fill: '#F5C4B3', stroke: '#993C1D', text: '#712B13' },  // coral
  { fill: '#C0DD97', stroke: '#3B6D11', text: '#27500A' },  // lime
  { fill: '#F4C0D1', stroke: '#993556', text: '#72243E' },  // pink
  { fill: '#CECBF6', stroke: '#534AB7', text: '#3C3489' },  // purple
];

// Isolated Search Input component - MUST be defined OUTSIDE parent to prevent focus loss
// This component manages its own state completely independently
const ProductSearchInput = React.memo(({ availableProducts, onSelectProduct }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Click-outside handler
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (!val) { 
      setResults([]); 
      setShowDropdown(false); 
      return; 
    }
    const q = val.toLowerCase().trim();
    const exact = availableProducts.filter(p => p.sku?.toString() === q);
    const startsWith = availableProducts.filter(p => 
      p.sku?.toString().startsWith(q) && p.sku?.toString() !== q
    );
    const contains = availableProducts.filter(p => 
      !p.sku?.toString().startsWith(q) && 
      (p.sku?.toString().includes(q) || p.name?.toLowerCase().includes(q))
    );
    const filtered = [...exact, ...startsWith, ...contains].slice(0, 8);
    setResults(filtered);
    setShowDropdown(filtered.length > 0);
  };

  const handleSelect = (product) => {
    onSelectProduct(product);
    setQuery('');
    setResults([]);
    setShowDropdown(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  return (
    <div ref={containerRef} className="relative">
      <label className="text-gray-400 text-[10px] uppercase tracking-wide block mb-1">
        Add Products
      </label>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleChange}
          placeholder="Search by SKU or product name..."
          autoComplete="off"
          autoFocus={false}
          className="w-full text-sm h-10 pl-10 pr-4 bg-[#0d1b2a] text-white border border-gray-700 rounded-lg focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20 focus:outline-none"
          data-testid="product-search-input"
        />
        
        {/* Search Results Dropdown */}
        {showDropdown && (
          <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-slate-800 border border-gray-700 rounded-lg shadow-xl max-h-72 overflow-y-auto">
            {results.map((product, idx) => {
              const hasSpecs = product.carton_specs?.units_per_carton > 0 || product.carton_specs?.units_per_master_carton > 0;
              const isFulex = product.fulfillment_channel === 'Fulex 3PL';
              return (
                <div
                  key={product.sku}
                  onMouseDown={(e) => { e.preventDefault(); handleSelect(product); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-700/50 transition-colors cursor-pointer ${idx === 0 ? 'bg-slate-700/30' : ''} ${idx < results.length - 1 ? 'border-b border-gray-700/50' : ''}`}
                  data-testid={`search-result-${product.sku}`}
                >
                  <span className="text-blue-400 font-mono text-sm font-medium w-16">{product.sku}</span>
                  <span className="text-white text-sm flex-1 truncate">{product.name}</span>
                  <div className="flex items-center gap-2">
                    {isFulex && (
                      <span className="text-[10px] bg-cyan-800/50 text-cyan-400 px-1.5 py-0.5 rounded">3PL</span>
                    )}
                    {!hasSpecs && (
                      <AlertCircle className="w-4 h-4 text-yellow-500" title="No carton specs" />
                    )}
                    <Plus className="w-4 h-4 text-gray-400" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {!query && (
        <p className="text-gray-500 text-xs mt-2">Type to search {availableProducts.length} products</p>
      )}
    </div>
  );
});

const ShipmentPlannerTab = ({ tenantId: propTenantId, providers = [] }) => {
  // Get tenant ID from props or localStorage (same pattern as other components)
  const tenantId = propTenantId || localStorage.getItem('tenant_id') || 'tenant-123';
  
  // State
  const [plans, setPlans] = useState([]);
  const [products, setProducts] = useState([]);
  const [forecastData, setForecastData] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [expandedPlanId, setExpandedPlanId] = useState(null);
  const [buildingPlan, setBuildingPlan] = useState(false);
  
  // Planning period state
  const [planningPeriod, setPlanningPeriod] = useState('monthly'); // 'monthly', 'quarterly', 'yearly'
  const [selectedQuarter, setSelectedQuarter] = useState('Q1'); // Q1, Q2, Q3, Q4
  const [selectedYear, setSelectedYear] = useState(2026);
  
  // Pallet plan state
  const [palletPlan, setPalletPlan] = useState(null);
  
  // Forecast suggestion labels (per SKU)
  const [forecastLabels, setForecastLabels] = useState({});
  
  // New/Edit plan form state
  const [planForm, setPlanForm] = useState({
    name: '',
    provider_name: '',
    target_date: '',
    notes: '',
    status: 'draft',
    months_of_stock: 3,
    safety_stock_pct: 15,
    items: []
  });
  
  // Fetch data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch shipment plans
      const plansRes = await api.get(`/api/v1/tpl/shipment-plans?tenant_id=${tenantId}`);
      setPlans(plansRes.data?.plans || plansRes.plans || []);
      
      // Fetch ALL products for shipment planning
      const productsRes = await api.get(`/api/v1/tpl/products?tenant_id=${tenantId}`);
      setProducts(productsRes.data?.products || productsRes.products || []);
    } catch (err) {
      console.error('Error loading shipment planner data:', err);
      toast.error('Failed to load shipment plans');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);
  
  useEffect(() => {
    loadData();
  }, [loadData]);
  
  // Fetch forecast data for selected products
  const fetchForecastForProducts = async (skus) => {
    if (!skus.length) return;
    try {
      const res = await api.get(`/api/v1/tpl/shipment-forecast?tenant_id=${tenantId}&skus=${skus.join(',')}`);
      const forecasts = res.data?.forecasts || res.forecasts || [];
      const map = {};
      forecasts.forEach(f => {
        map[f.sku] = f;
      });
      setForecastData(prev => ({ ...prev, ...map }));
    } catch (err) {
      console.error('Error fetching forecast:', err);
    }
  };
  
  // Start editing a plan
  const startEditPlan = (plan) => {
    setEditingPlanId(plan.id);
    setPlanForm({
      name: plan.name || '',
      provider_name: plan.provider_name || '',
      target_date: plan.target_date || '',
      notes: plan.notes || '',
      status: plan.status || 'draft',
      months_of_stock: plan.forecast_inputs?.months_of_stock || 3,
      safety_stock_pct: plan.forecast_inputs?.safety_stock_pct || 15,
      items: (plan.items || []).map((item, idx) => ({
        ...item,
        color: SKU_COLORS[idx % SKU_COLORS.length]
      }))
    });
    setExpandedPlanId(plan.id);
    setPalletPlan(plan.pallet_plan ? {
      pallets: plan.pallet_plan,
      total_pallets: plan.pallet_plan?.length || 0,
      dedicated_count: plan.pallet_plan?.filter(p => p.type === 'DEDICATED').length || 0,
      mixed_count: plan.pallet_plan?.filter(p => p.type === 'MIXED').length || 0,
      packing_slips: plan.packing_slips || [],
      container_recommendation: plan.container_recommendation,
      container_cost: plan.container_cost
    } : null);
    
    // Fetch forecast for items
    if (plan.items?.length) {
      fetchForecastForProducts(plan.items.map(i => i.sku));
    }
  };
  
  // Cancel editing
  const cancelEdit = () => {
    setEditingPlanId(null);
    resetPlanForm();
    setPalletPlan(null);
  };
  
  // Add product to plan form with auto-filled forecast - memoized
  // ALWAYS makes fresh API call - no caching
  const addProductToPlan = useCallback(async (product) => {
    if (planForm.items.some(item => item.sku === product.sku)) {
      toast.error('Product already added to plan');
      return;
    }
    
    // Fetch forecast suggestion from the API - ALWAYS fresh call, no cache
    let forecastSuggestion = { avg_monthly_units: 0, label: 'No history', has_history: false };
    try {
      // api.get already returns response.data directly
      const data = await api.get(`/api/v1/tpl/forecast-suggestion?sku=${product.sku}&months=6&tenant_id=${tenantId}`);
      console.log('Forecast API response for', product.sku, ':', data);
      
      if (data && typeof data === 'object') {
        forecastSuggestion = {
          avg_monthly_units: data.avg_monthly_units || 0,
          label: data.label || 'No history',
          has_history: data.has_history || false
        };
      }
    } catch (err) {
      console.warn('Failed to fetch forecast suggestion:', err);
      // Fall back to existing forecast data
      await fetchForecastForProducts([product.sku]);
      const forecast = forecastData[product.sku] || {};
      forecastSuggestion = {
        avg_monthly_units: forecast.avg_monthly_units || 0,
        label: forecast.avg_monthly_units > 0 ? '6mo avg' : 'No history',
        has_history: forecast.avg_monthly_units > 0
      };
    }
    
    console.log('Final forecastSuggestion:', forecastSuggestion);
    
    // Store the label for this SKU - ALWAYS set the label
    setForecastLabels(prev => ({
      ...prev,
      [product.sku]: forecastSuggestion.label
    }));
    
    const specs = product.carton_specs || {};
    const unitsPerCarton = specs.units_per_master_carton || specs.units_per_carton || 0;
    const cartonsPerPalletLayer = specs.cartons_per_pallet_layer || 4;
    const layersPerPallet = specs.layers_per_pallet || 4;
    const cartonsPerPallet = specs.cartons_per_pallet || (cartonsPerPalletLayer * layersPerPallet);
    
    // Create new item with forecast value populated
    const newItem = {
      product_id: product.id,
      sku: product.sku,
      name: product.name,
      monthly_forecast: forecastSuggestion.avg_monthly_units,  // Populate the input
      forecast_source: forecastSuggestion.has_history ? 'history' : 'manual',
      units_per_carton: unitsPerCarton,
      cartons_per_pallet_layer: cartonsPerPalletLayer,
      layers_per_pallet: layersPerPallet,
      cartons_per_pallet: cartonsPerPallet,
      carton_height_inches: specs.carton_height_in || specs.height || 10,
      carton_weight_lbs: specs.carton_weight_lb || specs.weight || 18,
      fulfillment_channel: product.fulfillment_channel || '',
      color: SKU_COLORS[planForm.items.length % SKU_COLORS.length]
    };
    
    console.log('New item monthly_forecast:', newItem.monthly_forecast);
    
    setPlanForm(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  }, [planForm.items, forecastData, fetchForecastForProducts, tenantId]);
  
  // Calculate derived values for an item
  // Guards against NaN when cartons_per_pallet is 0/null/undefined
  const calculateItemValues = (item, monthsOfStock, safetyPct) => {
    const forecast = item.monthly_forecast || 0;
    const unitsPerCarton = item.units_per_carton || 0;
    const cartonsPerPallet = item.cartons_per_pallet || 0;
    
    const totalPackets = Math.ceil(forecast * monthsOfStock * (1 + safetyPct / 100));
    const cartonsNeeded = unitsPerCarton > 0 ? Math.ceil(totalPackets / unitsPerCarton) : 0;
    
    // Guard against division by zero - return 0 if cartons_per_pallet is not set
    const fullPallets = cartonsPerPallet > 0 ? Math.floor(cartonsNeeded / cartonsPerPallet) : 0;
    const remainingCartons = cartonsPerPallet > 0 ? cartonsNeeded - (fullPallets * cartonsPerPallet) : cartonsNeeded;
    
    return {
      total_packets: totalPackets || 0,
      cartons_needed: cartonsNeeded || 0,
      full_pallets: fullPallets || 0,
      remaining_cartons: remainingCartons || 0
    };
  };
  
  // Update item field
  const updateItem = (index, field, value) => {
    setPlanForm(prev => {
      const items = [...prev.items];
      items[index] = { ...items[index], [field]: value };
      
      // If updating cartons_per_pallet_layer or layers_per_pallet, recalculate cartons_per_pallet
      if (field === 'cartons_per_pallet_layer' || field === 'layers_per_pallet') {
        const layer = items[index].cartons_per_pallet_layer || 0;
        const layers = items[index].layers_per_pallet || 0;
        items[index].cartons_per_pallet = layer * layers;
      }
      
      return { ...prev, items };
    });
    // Clear pallet plan when items change
    setPalletPlan(null);
  };
  
  // Remove item from plan
  const removeItemFromPlan = (index) => {
    setPlanForm(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index).map((item, idx) => ({
        ...item,
        color: SKU_COLORS[idx % SKU_COLORS.length]
      }))
    }));
    setPalletPlan(null);
  };
  
  // Build pallet plan
  const buildPalletPlan = async () => {
    if (!editingPlanId && !showCreateForm) {
      toast.error('Please create or edit a plan first');
      return;
    }
    
    if (planForm.items.length === 0) {
      toast.error('Add products to the plan first');
      return;
    }
    
    // Validate all items have required specs
    const missingSpecs = planForm.items.filter(item => 
      !item.units_per_carton || !item.cartons_per_pallet || !item.monthly_forecast
    );
    if (missingSpecs.length > 0) {
      toast.error(`Fill in Monthly Forecast, Units/Carton, and Cartons/Pallet for all products`);
      return;
    }
    
    setBuildingPlan(true);
    try {
      // First save the plan if creating new
      let planId = editingPlanId;
      if (!planId) {
        const createRes = await api.post(`/api/v1/tpl/shipment-plans?tenant_id=${tenantId}`, {
          name: planForm.name || `Shipment Plan ${new Date().toLocaleDateString()}`,
          provider_name: planForm.provider_name,
          target_date: planForm.target_date,
          notes: planForm.notes,
          status: planForm.status,
          items: planForm.items.map(item => ({
            sku: item.sku,
            name: item.name,
            monthly_forecast: item.monthly_forecast,
            units_per_carton: item.units_per_carton,
            cartons_per_pallet: item.cartons_per_pallet
          }))
        });
        planId = createRes.data?.id || createRes.id;
        setEditingPlanId(planId);
      }
      
      // Build pallet plan
      const res = await api.post(`/api/v1/tpl/shipment-plans/${planId}/build-pallet-plan?tenant_id=${tenantId}`, {
        months_of_stock: planForm.months_of_stock,
        safety_stock_pct: planForm.safety_stock_pct,
        products: planForm.items.map(item => ({
          sku: item.sku,
          monthly_forecast: item.monthly_forecast,
          units_per_carton: item.units_per_carton,
          cartons_per_pallet: item.cartons_per_pallet,
          cartons_per_pallet_layer: item.cartons_per_pallet_layer || 4,
          layers_per_pallet: item.layers_per_pallet || 4,
          carton_height_inches: item.carton_height_inches || 10,
          carton_weight_lbs: item.carton_weight_lbs || 18
        }))
      });
      
      const result = res.data || res;
      setPalletPlan(result);
      toast.success(`Pallet plan built: ${result.total_pallets} pallets`);
      loadData();
    } catch (err) {
      console.error('Error building pallet plan:', err);
      toast.error('Failed to build pallet plan');
    } finally {
      setBuildingPlan(false);
    }
  };
  
  // Copy packing slips to clipboard
  const copyPackingSlips = () => {
    if (!palletPlan?.packing_slips) return;
    const text = palletPlan.packing_slips.map(s => s.content).join('\n\n---\n\n');
    navigator.clipboard.writeText(text);
    toast.success('Packing slips copied to clipboard');
  };
  
  // Download packing slips as TXT
  const downloadPackingSlips = () => {
    if (!palletPlan?.packing_slips) return;
    const text = palletPlan.packing_slips.map(s => s.content).join('\n\n---\n\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `packing_slips_${planForm.name || 'shipment'}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Packing slips downloaded');
  };
  
  // Reset form
  const resetPlanForm = () => {
    setPlanForm({
      name: '',
      provider_name: '',
      target_date: '',
      notes: '',
      status: 'draft',
      months_of_stock: 3,
      safety_stock_pct: 15,
      items: []
    });
    setShowCreateForm(false);
    setPalletPlan(null);
  };
  
  // Save plan
  const savePlan = async () => {
    if (!planForm.name.trim()) {
      toast.error('Plan name is required');
      return;
    }
    
    try {
      const payload = {
        name: planForm.name,
        provider_name: planForm.provider_name,
        target_date: planForm.target_date,
        notes: planForm.notes,
        status: planForm.status,
        items: planForm.items.map(item => ({
          sku: item.sku,
          name: item.name,
          monthly_forecast: item.monthly_forecast,
          units_per_carton: item.units_per_carton,
          cartons_per_pallet: item.cartons_per_pallet,
          cartons_per_pallet_layer: item.cartons_per_pallet_layer,
          layers_per_pallet: item.layers_per_pallet
        })),
        forecast_inputs: {
          months_of_stock: planForm.months_of_stock,
          safety_stock_pct: planForm.safety_stock_pct
        }
      };
      
      if (editingPlanId) {
        await api.patch(`/api/v1/tpl/shipment-plans/${editingPlanId}?tenant_id=${tenantId}`, payload);
        toast.success('Plan updated successfully');
      } else {
        await api.post(`/api/v1/tpl/shipment-plans?tenant_id=${tenantId}`, payload);
        toast.success('Plan created successfully');
      }
      
      loadData();
      resetPlanForm();
      setEditingPlanId(null);
    } catch (err) {
      console.error('Error saving plan:', err);
      toast.error('Failed to save plan');
    }
  };
  
  // Delete plan
  const deletePlan = async (planId) => {
    if (!window.confirm('Delete this shipment plan?')) return;
    try {
      await api.delete(`/api/v1/tpl/shipment-plans/${planId}?tenant_id=${tenantId}`);
      toast.success('Plan deleted');
      loadData();
    } catch (err) {
      toast.error('Failed to delete plan');
    }
  };
  
  // Available products (not already in plan) - memoized to prevent unnecessary re-renders
  const availableProducts = React.useMemo(() => 
    products.filter(p => !planForm.items.some(item => item.sku === p.sku)),
    [products, planForm.items]
  );

  // Select handler - memoized callback for ProductSearchInput component
  const handleSelectProduct = useCallback((product) => {
    addProductToPlan(product);
  }, [addProductToPlan]);

  // Calculate totals
  const totals = planForm.items.reduce((acc, item) => {
    const calc = calculateItemValues(item, planForm.months_of_stock, planForm.safety_stock_pct);
    return {
      totalPackets: acc.totalPackets + calc.total_packets,
      totalCartons: acc.totalCartons + calc.cartons_needed,
      fullPallets: acc.fullPallets + calc.full_pallets,
      remainingCartons: acc.remainingCartons + calc.remaining_cartons
    };
  }, { totalPackets: 0, totalCartons: 0, fullPallets: 0, remainingCartons: 0 });
  
  // Pallet Card Component
  const PalletCard = ({ pallet, totalPallets }) => {
    const isDedicated = pallet.type === 'DEDICATED';
    return (
      <div className={`rounded-lg border ${isDedicated ? 'border-blue-600/50 bg-blue-900/10' : 'border-amber-600/50 bg-amber-900/10'} overflow-hidden`}>
        {/* Header */}
        <div className={`px-3 py-2 text-xs font-medium flex items-center justify-between ${isDedicated ? 'bg-blue-800/30 text-blue-300' : 'bg-amber-800/30 text-amber-300'}`}>
          <span>Pallet {pallet.number} of {totalPallets}</span>
          <span className={`px-1.5 py-0.5 rounded text-[10px] ${isDedicated ? 'bg-blue-700/50' : 'bg-amber-700/50'}`}>
            {pallet.type}
          </span>
        </div>
        
        {/* Layers visualization */}
        <div className="p-2 space-y-1">
          {pallet.layers?.slice().reverse().map((layer, idx) => (
            <div 
              key={idx}
              className="h-5 rounded text-[9px] flex items-center justify-center font-medium"
              style={{ 
                backgroundColor: layer.color?.fill || '#666',
                color: layer.color?.text || '#fff',
                border: `1px solid ${layer.color?.stroke || '#444'}`
              }}
            >
              {layer.sku?.substring(0, 12)} L{layer.layer_num}
            </div>
          ))}
          {/* Pallet base */}
          <div className="h-3 rounded bg-gray-700/50 text-[8px] text-gray-500 flex items-center justify-center">
            BASE
          </div>
        </div>
        
        {/* Stats */}
        <div className="px-3 py-2 border-t border-gray-700/50 text-[10px] text-gray-400 space-y-0.5">
          <div>{pallet.total_cartons} ctns · {pallet.total_packets?.toLocaleString()} pkts</div>
          <div>{pallet.height_inches}" tall · ~{pallet.weight_lbs?.toLocaleString()} lbs</div>
        </div>
        
        {/* Warning for mixed pallets */}
        {pallet.requires_slip_sheet && (
          <div className="px-2 py-1.5 bg-amber-900/30 text-amber-400 text-[9px] flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            Slip sheet required between layers
          </div>
        )}
      </div>
    );
  };
  
  // Plan Form UI
  const PlanFormUI = () => (
    <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 space-y-4">
      {/* Header Row */}
      <div className="grid grid-cols-4 gap-3">
        <div>
          <label className="text-gray-400 text-[10px] uppercase tracking-wide block mb-1">Plan Name *</label>
          <input
            type="text"
            value={planForm.name}
            onChange={(e) => setPlanForm(prev => ({ ...prev, name: e.target.value }))}
            placeholder="Q1 2026 Container"
            className="w-full text-xs h-8 px-2 bg-[#0d1b2a] text-white border border-gray-700 rounded focus:border-cyan-500"
            data-testid="plan-name-input"
          />
        </div>
        <div>
          <label className="text-gray-400 text-[10px] uppercase tracking-wide block mb-1">3PL Provider</label>
          <select
            value={planForm.provider_name}
            onChange={(e) => setPlanForm(prev => ({ ...prev, provider_name: e.target.value }))}
            className="w-full text-xs h-8 px-2 bg-[#0d1b2a] text-white border border-gray-700 rounded focus:border-cyan-500"
          >
            <option value="">Select Provider</option>
            {providers.map(p => (
              <option key={p.id} value={p.provider_name}>{p.provider_name}</option>
            ))}
            <option value="Fulex">Fulex</option>
          </select>
        </div>
        <div>
          <label className="text-gray-400 text-[10px] uppercase tracking-wide block mb-1">Target Date</label>
          <input
            type="date"
            value={planForm.target_date}
            onChange={(e) => setPlanForm(prev => ({ ...prev, target_date: e.target.value }))}
            className="w-full text-xs h-8 px-2 bg-[#0d1b2a] text-white border border-gray-700 rounded focus:border-cyan-500"
          />
        </div>
        <div>
          <label className="text-gray-400 text-[10px] uppercase tracking-wide block mb-1">Status</label>
          <select
            value={planForm.status}
            onChange={(e) => setPlanForm(prev => ({ ...prev, status: e.target.value }))}
            className="w-full text-xs h-8 px-2 bg-[#0d1b2a] text-white border border-gray-700 rounded focus:border-cyan-500"
          >
            <option value="draft">Draft</option>
            <option value="planned">Planned</option>
            <option value="in_transit">In Transit</option>
            <option value="received">Received</option>
          </select>
        </div>
      </div>
      
      {/* Forecast Controls */}
      <div className="flex items-center gap-6 py-2 border-y border-gray-700/50">
        {/* Planning Period Selector */}
        <div className="flex items-center gap-2">
          <label className="text-gray-400 text-xs">Planning Period:</label>
          <div className="flex items-center gap-1 bg-[#0d1b2a] rounded border border-gray-700 p-0.5">
            <button
              onClick={() => setPlanningPeriod('monthly')}
              className={`px-3 py-1 text-xs rounded transition-colors ${
                planningPeriod === 'monthly' 
                  ? 'bg-cyan-600 text-white' 
                  : 'text-gray-400 hover:text-white'
              }`}
              data-testid="period-monthly-btn"
            >
              Monthly
            </button>
            <button
              onClick={() => setPlanningPeriod('quarterly')}
              className={`px-3 py-1 text-xs rounded transition-colors ${
                planningPeriod === 'quarterly' 
                  ? 'bg-cyan-600 text-white' 
                  : 'text-gray-400 hover:text-white'
              }`}
              data-testid="period-quarterly-btn"
            >
              Quarterly
            </button>
            <button
              onClick={() => setPlanningPeriod('yearly')}
              className={`px-3 py-1 text-xs rounded transition-colors ${
                planningPeriod === 'yearly' 
                  ? 'bg-cyan-600 text-white' 
                  : 'text-gray-400 hover:text-white'
              }`}
              data-testid="period-yearly-btn"
            >
              Yearly
            </button>
          </div>
        </div>
        
        {/* Quarterly Selector (shown when quarterly is selected) */}
        {planningPeriod === 'quarterly' && (
          <div className="flex items-center gap-2">
            <label className="text-gray-400 text-xs">Quarter:</label>
            <select
              value={selectedQuarter}
              onChange={(e) => {
                setSelectedQuarter(e.target.value);
                // Auto-set months_of_stock to 3 for quarterly
                setPlanForm(prev => ({ ...prev, months_of_stock: 3 }));
                setPalletPlan(null);
              }}
              className="text-xs h-7 px-2 bg-[#0d1b2a] text-white border border-gray-700 rounded focus:border-cyan-500"
              data-testid="quarter-select"
            >
              <option value="Q1">Q1 {selectedYear}</option>
              <option value="Q2">Q2 {selectedYear}</option>
              <option value="Q3">Q3 {selectedYear}</option>
              <option value="Q4">Q4 {selectedYear}</option>
            </select>
            <select
              value={selectedYear}
              onChange={(e) => {
                setSelectedYear(parseInt(e.target.value));
                setPalletPlan(null);
              }}
              className="text-xs h-7 px-2 bg-[#0d1b2a] text-white border border-gray-700 rounded focus:border-cyan-500"
            >
              <option value={2025}>2025</option>
              <option value={2026}>2026</option>
              <option value={2027}>2027</option>
            </select>
          </div>
        )}
        
        {/* Monthly Selector (shown when monthly is selected) */}
        {planningPeriod === 'monthly' && (
          <div className="flex items-center gap-2">
            <label className="text-gray-400 text-xs">Months of stock:</label>
            <select
              value={planForm.months_of_stock}
              onChange={(e) => {
                setPlanForm(prev => ({ ...prev, months_of_stock: parseInt(e.target.value) }));
                setPalletPlan(null);
              }}
              className="text-xs h-7 px-2 bg-[#0d1b2a] text-white border border-gray-700 rounded focus:border-cyan-500"
              data-testid="months-of-stock-select"
            >
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
              <option value={4}>4</option>
              <option value={5}>5</option>
              <option value={6}>6</option>
              <option value={9}>9</option>
              <option value={12}>12</option>
            </select>
          </div>
        )}
        
        {/* Yearly indicator (shown when yearly is selected) */}
        {planningPeriod === 'yearly' && (
          <div className="flex items-center gap-2">
            <label className="text-gray-400 text-xs">Year:</label>
            <select
              value={selectedYear}
              onChange={(e) => {
                setSelectedYear(parseInt(e.target.value));
                // Auto-set months_of_stock to 12 for yearly
                setPlanForm(prev => ({ ...prev, months_of_stock: 12 }));
                setPalletPlan(null);
              }}
              className="text-xs h-7 px-2 bg-[#0d1b2a] text-white border border-gray-700 rounded focus:border-cyan-500"
            >
              <option value={2025}>2025</option>
              <option value={2026}>2026</option>
              <option value={2027}>2027</option>
            </select>
            <span className="text-xs text-gray-500">(12 months)</span>
          </div>
        )}
        
        <div className="flex items-center gap-2">
          <label className="text-gray-400 text-xs">Safety stock %:</label>
          <input
            type="number"
            value={planForm.safety_stock_pct}
            onChange={(e) => {
              setPlanForm(prev => ({ ...prev, safety_stock_pct: parseFloat(e.target.value) || 0 }));
              setPalletPlan(null);
            }}
            className="w-16 text-xs h-7 px-2 bg-[#0d1b2a] text-white border border-gray-700 rounded focus:border-cyan-500 text-center"
            data-testid="safety-stock-input"
          />
        </div>
        
        <button
          onClick={buildPalletPlan}
          disabled={buildingPlan || planForm.items.length === 0}
          className="flex items-center gap-2 px-4 py-1.5 bg-cyan-600 hover:bg-cyan-500 disabled:bg-gray-600 text-white text-xs font-medium rounded transition-colors"
          data-testid="build-pallet-plan-btn"
        >
          {buildingPlan ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Calculator className="w-3.5 h-3.5" />}
          Build Pallet Plan
        </button>
        
        {planForm.items.length > 0 && (
          <div className="ml-auto text-xs text-gray-400 flex items-center gap-4">
            <span className="text-cyan-400 font-medium">{totals.fullPallets + (totals.remainingCartons > 0 ? 1 : 0)} pallets</span>
            <span>·</span>
            <span>{totals.totalCartons.toLocaleString()} cartons</span>
            <span>·</span>
            <span>{totals.totalPackets.toLocaleString()} packets</span>
          </div>
        )}
      </div>
      
      {/* Product Search - Isolated Component to prevent focus loss */}
      <ProductSearchInput 
        availableProducts={availableProducts} 
        onSelectProduct={handleSelectProduct} 
      />
      
      {/* Items Table */}
      {planForm.items.length > 0 && (
        <div className="border border-gray-700 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-800">
              <tr>
                <th className="text-left text-gray-400 px-3 py-3 text-xs font-medium w-20">SKU</th>
                <th className="text-left text-gray-400 px-3 py-3 text-xs font-medium">Product</th>
                <th className="text-right text-gray-400 px-3 py-3 text-xs font-medium w-28">
                  <div className="flex items-center justify-end gap-1">
                    Monthly Fcst
                    <BarChart3 className="w-3 h-3 text-cyan-500" />
                  </div>
                </th>
                <th className="text-right text-gray-400 px-3 py-3 text-xs font-medium w-24">Units/Ctn</th>
                <th className="text-right text-gray-400 px-3 py-3 text-xs font-medium w-24">Ctns/Pallet</th>
                <th className="text-right text-gray-400 px-3 py-3 text-xs font-medium w-24">Total Pkts</th>
                <th className="text-right text-cyan-400 px-3 py-3 text-xs font-medium w-24">Ctns Need</th>
                <th className="text-right text-cyan-400 px-3 py-3 text-xs font-medium w-20">Full Plt</th>
                <th className="text-right text-amber-400 px-3 py-3 text-xs font-medium w-20">Rem Ctns</th>
                <th className="text-center text-gray-400 px-2 py-3 text-xs font-medium w-10"></th>
              </tr>
            </thead>
            <tbody>
              {planForm.items.map((item, index) => {
                const calc = calculateItemValues(item, planForm.months_of_stock, planForm.safety_stock_pct);
                const needsSpecs = !item.units_per_carton || !item.cartons_per_pallet;
                
                return (
                  <tr key={item.sku} className={`border-t border-gray-700 hover:bg-slate-800/50 ${needsSpecs ? 'bg-yellow-900/10' : ''}`}>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-sm flex-shrink-0" 
                          style={{ backgroundColor: item.color?.fill, border: `1px solid ${item.color?.stroke}` }}
                        />
                        <span className="text-blue-400 font-medium text-sm">{item.sku}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-white text-sm">{item.name?.substring(0, 30)}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-col">
                        <div className="flex items-center justify-end gap-2">
                          <input
                            type="number"
                            value={item.monthly_forecast || ''}
                            onChange={(e) => {
                              updateItem(index, 'monthly_forecast', parseInt(e.target.value) || 0);
                              // Clear the label when user manually edits
                              setForecastLabels(prev => {
                                const newLabels = { ...prev };
                                delete newLabels[item.sku];
                                return newLabels;
                              });
                            }}
                            className="w-full text-sm text-right h-9 px-2 bg-[#0d1b2a] text-white border border-gray-700 rounded focus:border-cyan-500 focus:outline-none"
                            tabIndex={index * 3 + 1}
                            data-testid={`forecast-input-${item.sku}`}
                          />
                        </div>
                        {/* Forecast label - shows source of data */}
                        {forecastLabels[item.sku] && (
                          <span className={`text-[9px] text-right mt-0.5 ${
                            forecastLabels[item.sku] === 'No recent sales' 
                              ? 'text-amber-400' 
                              : 'text-gray-500'
                          }`}>
                            {forecastLabels[item.sku]}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        value={item.units_per_carton || ''}
                        onChange={(e) => updateItem(index, 'units_per_carton', parseInt(e.target.value) || 0)}
                        placeholder="0"
                        tabIndex={index * 3 + 2}
                        className={`w-full text-sm text-right h-9 px-2 rounded focus:outline-none ${
                          needsSpecs 
                            ? 'bg-yellow-900/30 text-yellow-300 border border-yellow-700/50 focus:border-yellow-500' 
                            : 'bg-[#0d1b2a] text-white border border-gray-700 focus:border-cyan-500'
                        }`}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        value={item.cartons_per_pallet || ''}
                        onChange={(e) => updateItem(index, 'cartons_per_pallet', parseInt(e.target.value) || 0)}
                        placeholder="0"
                        tabIndex={index * 3 + 3}
                        className={`w-full text-sm text-right h-9 px-2 rounded focus:outline-none ${
                          needsSpecs 
                            ? 'bg-yellow-900/30 text-yellow-300 border border-yellow-700/50 focus:border-yellow-500' 
                            : 'bg-[#0d1b2a] text-white border border-gray-700 focus:border-cyan-500'
                        }`}
                      />
                    </td>
                    <td className="px-3 py-2 text-gray-300 text-sm text-right font-medium">
                      {isNaN(calc.total_packets) ? '—' : calc.total_packets.toLocaleString()}
                    </td>
                    <td className="px-3 py-2 text-cyan-400 text-sm text-right font-bold">
                      {isNaN(calc.cartons_needed) ? '—' : calc.cartons_needed}
                    </td>
                    <td className="px-3 py-2 text-cyan-400 text-sm text-right font-bold">
                      {!item.cartons_per_pallet ? '—' : (isNaN(calc.full_pallets) ? '—' : calc.full_pallets)}
                    </td>
                    <td className={`px-3 py-2 text-sm text-right font-bold ${calc.remaining_cartons > 0 ? 'text-amber-400' : 'text-gray-500'}`}>
                      {!item.cartons_per_pallet ? '—' : (isNaN(calc.remaining_cartons) ? '—' : calc.remaining_cartons)}
                    </td>
                    <td className="px-2 py-2 text-center">
                      <button
                        onClick={() => removeItemFromPlan(index)}
                        className="text-red-400 hover:text-red-300 p-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-slate-900 border-t border-gray-600">
              <tr>
                <td colSpan="5" className="px-3 py-3 text-gray-400 text-sm font-medium text-right">Totals:</td>
                <td className="px-3 py-3 text-white text-sm text-right font-bold">{totals.totalPackets.toLocaleString()}</td>
                <td className="px-3 py-3 text-cyan-400 text-sm text-right font-bold">{totals.totalCartons.toLocaleString()}</td>
                <td className="px-3 py-3 text-cyan-400 text-sm text-right font-bold">{totals.fullPallets}</td>
                <td className="px-3 py-3 text-amber-400 text-sm text-right font-bold">{totals.remainingCartons}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      
      {/* Pallet Plan Section */}
      {palletPlan && (
        <div className="space-y-4 border-t border-gray-700 pt-4">
          {/* Summary Bar */}
          <div className="flex items-center gap-4 text-xs bg-slate-900/50 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <Box className="w-4 h-4 text-cyan-500" />
              <span className="text-white font-medium">Total Pallets: {palletPlan.total_pallets}</span>
            </div>
            <div className="text-blue-400">Dedicated: {palletPlan.dedicated_count}</div>
            <div className="text-amber-400">Mixed: {palletPlan.mixed_count}</div>
            <div className="text-gray-400">Total Cartons: {palletPlan.total_cartons?.toLocaleString()}</div>
            <div className="ml-auto flex items-center gap-2 bg-green-900/30 text-green-400 px-3 py-1.5 rounded-lg">
              <Truck className="w-4 h-4" />
              <span>{palletPlan.container_recommendation} recommended (${palletPlan.container_cost} at Fulex)</span>
            </div>
          </div>
          
          {/* Pallet Cards Grid */}
          <div>
            <h4 className="text-gray-400 text-xs font-medium mb-2">Visual Pallet Layout</h4>
            <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
              {palletPlan.pallets?.map(pallet => (
                <PalletCard key={pallet.id} pallet={pallet} totalPallets={palletPlan.total_pallets} />
              ))}
            </div>
          </div>
          
          {/* Packing Slips Section */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-gray-400 text-xs font-medium">Packing Slips (Fulex Format)</h4>
              <div className="flex gap-2">
                <button
                  onClick={copyPackingSlips}
                  className="flex items-center gap-1.5 px-2 py-1 text-[10px] bg-slate-700 hover:bg-slate-600 text-white rounded"
                  data-testid="copy-packing-slips-btn"
                >
                  <Copy className="w-3 h-3" /> Copy
                </button>
                <button
                  onClick={downloadPackingSlips}
                  className="flex items-center gap-1.5 px-2 py-1 text-[10px] bg-slate-700 hover:bg-slate-600 text-white rounded"
                  data-testid="download-packing-slips-btn"
                >
                  <Download className="w-3 h-3" /> Download TXT
                </button>
              </div>
            </div>
            <div className="bg-slate-900 border border-gray-700 rounded p-3 max-h-48 overflow-y-auto">
              <pre className="text-[11px] text-gray-300 font-mono whitespace-pre-wrap">
                {palletPlan.packing_slips?.map((slip, idx) => (
                  <React.Fragment key={idx}>
                    {idx > 0 && '\n---\n\n'}
                    {slip.content}
                  </React.Fragment>
                ))}
              </pre>
            </div>
          </div>
        </div>
      )}
      
      {/* Actions */}
      <div className="flex justify-end gap-2 pt-2 border-t border-gray-700/50">
        <button
          onClick={editingPlanId ? cancelEdit : resetPlanForm}
          className="px-3 py-1.5 text-gray-400 hover:text-white text-xs"
        >
          Cancel
        </button>
        <button
          onClick={savePlan}
          className="flex items-center gap-1.5 px-4 py-1.5 bg-green-600 hover:bg-green-500 text-white text-xs font-medium rounded"
          data-testid="save-plan-btn"
        >
          <Save className="w-3.5 h-3.5" />
          {editingPlanId ? 'Update Plan' : 'Save Plan'}
        </button>
      </div>
    </div>
  );
  
  // Existing Plan Card
  const PlanCard = ({ plan }) => {
    const isExpanded = expandedPlanId === plan.id;
    const isEditing = editingPlanId === plan.id;
    const totalUnits = (plan.items || []).reduce((sum, item) => sum + (item.units_allocated || 0), 0);
    const totalCartons = (plan.items || []).reduce((sum, item) => sum + (item.cartons_needed || 0), 0);
    const hasPalletPlan = plan.pallet_plan && plan.pallet_plan.length > 0;
    
    const statusColors = {
      draft: 'bg-gray-600/30 text-gray-400',
      planned: 'bg-blue-600/30 text-blue-400',
      in_transit: 'bg-yellow-600/30 text-yellow-400',
      received: 'bg-green-600/30 text-green-400'
    };
    
    return (
      <div className="bg-slate-800/30 border border-slate-700 rounded-lg overflow-hidden">
        {/* Header */}
        <div 
          className="flex items-center justify-between p-3 cursor-pointer hover:bg-slate-800/50"
          onClick={() => setExpandedPlanId(isExpanded ? null : plan.id)}
          data-testid={`plan-card-${plan.id}`}
        >
          <div className="flex items-center gap-3">
            {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
            <Layers className="w-4 h-4 text-cyan-500" />
            <div>
              <div className="text-white text-sm font-medium">{plan.name}</div>
              <div className="text-gray-500 text-[10px]">
                {plan.provider_name || 'No provider'} · {plan.items?.length || 0} products
                {hasPalletPlan && ` · ${plan.pallet_plan.length} pallets`}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`px-2 py-0.5 rounded text-[10px] ${statusColors[plan.status] || statusColors.draft}`}>
              {plan.status?.replace('_', ' ')}
            </span>
            {hasPalletPlan && (
              <span className="px-2 py-0.5 rounded text-[10px] bg-cyan-800/30 text-cyan-400">
                Plan Built
              </span>
            )}
            <div className="flex gap-1">
              <button
                onClick={(e) => { e.stopPropagation(); startEditPlan(plan); }}
                className="p-1.5 text-gray-400 hover:text-white rounded hover:bg-slate-700"
              >
                <Edit2 className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); deletePlan(plan.id); }}
                className="p-1.5 text-gray-400 hover:text-red-400 rounded hover:bg-slate-700"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
        
        {/* Expanded content - Show edit form */}
        {isExpanded && isEditing && <div className="p-3 border-t border-slate-700">{PlanFormUI()}</div>}
        
        {/* Expanded content - View only */}
        {isExpanded && !isEditing && (
          <div className="p-3 border-t border-slate-700 space-y-3">
            {/* Plan items summary */}
            {plan.items?.length > 0 && (
              <div className="text-xs">
                <div className="text-gray-400 mb-1">Products in plan:</div>
                <div className="flex flex-wrap gap-2">
                  {plan.items.map((item, idx) => (
                    <span key={item.sku} className="px-2 py-1 bg-slate-700/50 rounded text-gray-300">
                      {item.sku}: {item.monthly_forecast || item.units_allocated || 0}/mo
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Pallet plan summary */}
            {hasPalletPlan && (
              <div className="text-xs bg-slate-900/50 rounded p-2">
                <div className="flex items-center gap-4">
                  <span className="text-cyan-400 font-medium">{plan.pallet_plan.length} pallets</span>
                  <span className="text-gray-400">·</span>
                  <span className="text-gray-400">{plan.pallet_plan.filter(p => p.type === 'DEDICATED').length} dedicated</span>
                  <span className="text-gray-400">·</span>
                  <span className="text-amber-400">{plan.pallet_plan.filter(p => p.type === 'MIXED').length} mixed</span>
                  {plan.container_recommendation && (
                    <>
                      <span className="text-gray-400">·</span>
                      <span className="text-green-400">{plan.container_recommendation} container</span>
                    </>
                  )}
                </div>
              </div>
            )}
            
            <button
              onClick={() => startEditPlan(plan)}
              className="text-cyan-400 hover:text-cyan-300 text-xs flex items-center gap-1"
            >
              <Edit2 className="w-3 h-3" /> Edit Plan
            </button>
          </div>
        )}
      </div>
    );
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <RefreshCw className="w-5 h-5 text-cyan-500 animate-spin" />
        <span className="ml-2 text-gray-400 text-sm">Loading shipment planner...</span>
      </div>
    );
  }
  
  return (
    <div className="space-y-4" data-testid="shipment-planner-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-white text-sm font-semibold">Shipment Planner</h3>
          <p className="text-gray-400 text-xs mt-0.5">
            Forecast-driven pallet planning with visual layout + packing slips
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={loadData}
            className="p-2 text-gray-400 hover:text-white rounded hover:bg-slate-700"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          {!showCreateForm && !editingPlanId && (
            <button
              onClick={() => setShowCreateForm(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-medium rounded"
              data-testid="new-shipment-plan-btn"
            >
              <Plus className="w-3.5 h-3.5" />
              New Shipment Plan
            </button>
          )}
        </div>
      </div>
      
      {/* No Products Warning */}
      {products.length === 0 && (
        <div className="bg-yellow-900/20 border border-yellow-700/30 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-yellow-400 text-sm font-medium">No Products Found</h4>
              <p className="text-gray-400 text-xs mt-1">
                Add products in Product Master before creating shipment plans.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Create Form */}
      {showCreateForm && !editingPlanId && PlanFormUI()}
      
      {/* Existing Plans */}
      <div className="space-y-2">
        {plans.length > 0 ? (
          <>
            <div className="text-gray-500 text-[10px] uppercase tracking-wider">Saved Plans ({plans.length})</div>
            {plans.map(plan => (
              <PlanCard key={plan.id} plan={plan} />
            ))}
          </>
        ) : !showCreateForm && (
          <div className="text-center py-8 text-gray-500 text-sm">
            <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No shipment plans yet</p>
            <p className="text-xs">Click "New Shipment Plan" to get started</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShipmentPlannerTab;
